import React from 'react';
import PropTypes from 'prop-types';
import { Translate } from 'react-localize-redux';
import { ErrorBoundary } from 'react-error-boundary';
import Icon, { IconSizes } from '../../components/foundations/Icon';
import StatsTiles from './StatsTiles';
import BreakdownList from './BreakdownList';
import BreakdownTable from './BreakdownTable';
import TrendChart from './TrendChart';
import { BREAKDOWN_VARIANTS, TRANSLATION_KEY, VERDICT_OUTCOMES, VIEW_TYPES } from '../constants';
import { verdictShape } from '../propShapes';
import { formatNextRunLabel } from '../utils';
import css from './VerdictBody.css';

const CARD_KEY = `${TRANSLATION_KEY}.CARD`;

const BREAKDOWN_RENDERERS = {
    [BREAKDOWN_VARIANTS.LIST]: BreakdownList,
    [BREAKDOWN_VARIANTS.TABLE]: BreakdownTable,
};

const resolveViewRenderer = view => {
    if (!view) return null;
    if (view.type === VIEW_TYPES.STATS) return StatsTiles;
    if (view.type === VIEW_TYPES.BREAKDOWN) return BREAKDOWN_RENDERERS[view.variant] ?? null;
    if (view.type === VIEW_TYPES.TREND) return TrendChart;
    return null;
};

const OpenThreadLink = () => (
    <div className={css.openLink}>
        <Translate id={`${CARD_KEY}.OPEN_THREAD`} />
    </div>
);

const SystemVerdictBody = ({ translationKey }) => (
    <div>
        <div className={css.body}>
            <Translate id={`${CARD_KEY}.${translationKey}`} />
        </div>
        <OpenThreadLink />
    </div>
);

SystemVerdictBody.propTypes = {
    translationKey: PropTypes.string.isRequired,
};

const SuccessVerdictBody = ({ verdict }) => {
    const ViewRenderer = resolveViewRenderer(verdict.view);
    return (
        <div>
            <div className={css.body}>{verdict.headline}</div>
            {ViewRenderer && (
                <ErrorBoundary fallback={null}>
                    <ViewRenderer view={verdict.view} />
                </ErrorBoundary>
            )}
            <OpenThreadLink />
        </div>
    );
};

SuccessVerdictBody.propTypes = {
    verdict: verdictShape.isRequired,
};

const HeadlineVerdictBody = ({ verdict }) => (
    <div>
        <div className={css.mutedBody}>{verdict.headline}</div>
        <OpenThreadLink />
    </div>
);

HeadlineVerdictBody.propTypes = {
    verdict: verdictShape.isRequired,
};

const OUTCOME_RENDERERS = {
    [VERDICT_OUTCOMES.ERROR]: { Component: SystemVerdictBody, props: { translationKey: 'FAILED_BODY' } },
    [VERDICT_OUTCOMES.NO_VERDICT]: { Component: SystemVerdictBody, props: { translationKey: 'NO_VERDICT_BODY' } },
    [VERDICT_OUTCOMES.SUCCESS]: { Component: SuccessVerdictBody },
    [VERDICT_OUTCOMES.PROMPT_UNCLEAR]: { Component: HeadlineVerdictBody },
};

const VerdictBody = ({ isPromptUnclear, verdict = null, nextRunAt = null, checkInProgress = false }) => {
    if (isPromptUnclear) {
        return (
            <div>
                <div className={css.newMessageChip}>
                    <Icon name="message-circle" size={IconSizes.X_SMALL} interactive={false} />
                    <Translate id={`${CARD_KEY}.NEW_MESSAGE`} />
                </div>
                <div className={css.mutedBody}>{verdict.headline}</div>
                <div className={css.openLink}>
                    <Translate id={`${CARD_KEY}.OPEN_TO_REPLY`} />
                </div>
            </div>
        );
    }
    if (!verdict && checkInProgress) {
        return (
            <div className={css.checkingRow}>
                <span className={css.checkingSpinner} />
                <span className={css.mutedBody}>
                    <Translate id={`${CARD_KEY}.FIRST_CHECK_IN_PROGRESS`} />
                </span>
            </div>
        );
    }
    if (!verdict) {
        const firstRunLabel = formatNextRunLabel(nextRunAt);
        return (
            <div>
                <div className={css.pendingChip}>
                    <Translate id={`${CARD_KEY}.NO_RESULTS_YET`} />
                </div>
                <div className={css.mutedBody}>
                    {firstRunLabel ? (
                        <Translate id={`${CARD_KEY}.FIRST_RUN`} data={{ firstRunLabel }} />
                    ) : (
                        <Translate id={`${CARD_KEY}.NO_RUNS_YET`} />
                    )}
                </div>
            </div>
        );
    }
    const { Component, props = {} } = OUTCOME_RENDERERS[verdict.outcome] ?? { Component: HeadlineVerdictBody };
    return <Component verdict={verdict} {...props} />;
};

VerdictBody.propTypes = {
    isPromptUnclear: PropTypes.bool.isRequired,
    verdict: verdictShape,
    nextRunAt: PropTypes.string,
    checkInProgress: PropTypes.bool,
};

export default VerdictBody;
